/**
 * Rollen und Rechtekatalog -- F5-19.
 *
 * **Der Katalog kommt vom Server** und steht nicht ein zweites Mal hier. Er
 * waechst mit jedem Modul; eine Fassung im Client wuesste davon nichts. Was
 * hier zusaetzlich steht, sind nur die Beschriftungen -- und die laufen ueber
 * i18n, mit der Serverbeschreibung als Rueckfall fuer ein neues Recht, das
 * noch keinen Text hat.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';
import { ApiError } from '../../lib/apiError';
import { userKeys } from '../users/queries';

export type RoleRead = components['schemas']['RoleRead'];
export type PermissionRead = components['schemas']['PermissionRead'];

export const roleKeys = {
  list: ['roles'] as const,
  catalog: ['roles', 'permissions'] as const,
};

const keineWiederholungBei403 = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.list,
    queryFn: () => request<RoleRead[]>('/roles'),
    retry: keineWiederholungBei403,
  });
}

/** Der Katalog aendert sich nur mit einer neuen Fassung der Anwendung. */
export function usePermissionCatalog() {
  return useQuery({
    queryKey: roleKeys.catalog,
    queryFn: () => request<PermissionRead[]>('/roles/permissions'),
    staleTime: Infinity,
    retry: keineWiederholungBei403,
  });
}

export interface RolePayload {
  name: string;
  description: string;
  permissions: string[];
}

/**
 * Nach jeder Aenderung auch die Zugaenge neu holen: Die Traegerzahl und die
 * Auswahl unter "Zugaenge" haengen an derselben Liste.
 */
function useInvalidateRoles() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: roleKeys.list });
    await queryClient.invalidateQueries({ queryKey: userKeys.list });
  };
}

export function useCreateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (payload: RolePayload) =>
      request<RoleRead>('/roles', { method: 'POST', body: payload }),
    onSuccess: invalidate,
  });
}

export function useUpdateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: RolePayload }) =>
      request<RoleRead>(`/roles/${roleId}`, { method: 'PATCH', body: payload }),
    onSuccess: invalidate,
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (roleId: string) => request<undefined>(`/roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * Der angezeigte Name einer Rolle.
 *
 * Systemrollen tragen ihren Namen ueber i18n -- die Datenbank fuehrt ihn
 * deutsch und ohne Umlaute. Eigene Rollen heissen, wie ihr Mandant sie
 * genannt hat.
 */
export function roleLabel(role: RoleRead, t: TFunction): string {
  return role.is_system ? t(`role.${role.key}`, { defaultValue: role.name }) : role.name;
}

/** Die Beschreibung einer Rolle -- bei Systemrollen aus i18n, aus demselben Grund wie der Name. */
export function roleDescription(role: RoleRead, t: TFunction): string {
  return role.is_system
    ? t(`roleDescription.${role.key}`, { defaultValue: role.description })
    : role.description;
}
